import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useLocation, useParams } from 'react-router';
const { useCallback } = React;
import { TextingAutomationStep } from '../../../extViewmodels/sdk/models/Automation';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { getPrincipalInitials } from '../../../models/NameUtils';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	getDisplayNameForBulkEmail,
} from '../../../models/UiUtils';
import { useAutomationTemplateIdQuery } from '../../../queries';
import { AutoCompleteSearchField } from '../../components/autocomplete/AutoCompleteSearchField';
import { Avatar2 } from '../../components/Avatar2';
import { Checkbox } from '../../components/Checkbox';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { FabContext } from '../../components/FabContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { SendFromDropdown } from '../../components/SendFromDropdown';
import { SearchIcon } from '../../components/svgs/icons/SearchIcon';
import { baseStyleSheet as bs } from '../../styles/styles';
import { CampaignComposerBaseLayout } from '../BulkHwcPages/presentation';
import { useTextingCampaignContacts } from './hooks/useTextingCampaignContacts';
import { useTextingCampaignEditor } from './hooks/useTextingCampaignEditor';
import { PhoneMissingPill } from './PhoneMissingPill';
import { styleSheet } from './styles';
import { TextCampaignPageBody } from './TextCampaignPageBody';

interface RouteParams {
	draftId: string;
}

interface LocationState {
	contactIds: string[];
	initialSendOnBehalf?: Api.ISendOnBehalfPermission;
	initialSendFromUserId?: string;
	initialOwnershipFilter?: Api.IOwnershipFilter;
}

export function TextingCampaignDraftContactsPage() {
	const { draftId } = useParams<RouteParams>();
	const userSession = useUserSession();
	const location = useLocation<LocationState>();
	const fullscreenModal = useFullscreenModal();

	const templateQuery = useAutomationTemplateIdQuery({ id: draftId });

	// Load draft content for display and editing
	const [initialEditorState, setInitialEditorState] = React.useState<{
		initialCampaignTitle?: string;
		initialContentEditorState?: Api.IRichContentEditorState;
		initialAttachmentFiles?: File[];
		initialStoredAttachments?: Api.IFileAttachment[];
	}>({});

	const { state: editorState, dispatch: editorDispatch } = useTextingCampaignEditor(initialEditorState);

	// Initialize editor state when template loads
	React.useEffect(() => {
		if (templateQuery.data && !initialEditorState.initialCampaignTitle) {
			const template = templateQuery.data as Api.IAutomationTemplate;
			const steps = template.draft?.steps;

			if (steps && steps.length > 0) {
				const textingStep = steps.find((step: Api.IAutomationStep) => {
					return step._type === 'TextingAutomationStep';
				}) as TextingAutomationStep;

				if (textingStep) {
					let contentState;
					if (textingStep.content) {
						try {
							contentState = convertRawRichTextContentStateToRichContentEditorState(textingStep.content);
						} catch {
							contentState = undefined;
						}
					}

					const newEditorState = {
						initialCampaignTitle: template.name || '',
						initialContentEditorState: contentState,
						initialAttachmentFiles: [] as File[],
						initialStoredAttachments: textingStep.attachments || [],
					};

					setInitialEditorState(newEditorState);

					if (contentState) {
						editorDispatch({ type: 'setTextingContentEditorState', value: contentState });
					}
					editorDispatch({ type: 'setCampaignTitle', value: template.name || '' });
					// Initialize stored attachments from draft template
					editorDispatch({ type: 'setStoredAttachments', value: textingStep.attachments || [] });
				}
			}
		}
	}, [templateQuery.data, initialEditorState.initialCampaignTitle, editorDispatch]);

	const { state, dispatch, contactQueries, numberOfRecipients } = useTextingCampaignContacts({
		initialContactIds: location.state?.contactIds,
	});

	const [sendFrom, setSendFrom] = React.useState<Api.ISendOnBehalfPermission>(
		location.state?.initialSendOnBehalf ?? userSession.defaultSendOnBehalfPermissions[0]
	);
	const [sendFromUserId, setSendFromUserId] = React.useState<string | null>(
		location.state?.initialSendFromUserId ?? null
	);

	const onSendOnBehalf = useCallback((option?: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		setSendFrom(option);
		setSendFromUserId(user?.id ?? null);
	}, []);

	const handleRemoveRecipient = useCallback(
		(contact: Api.IContact) => {
			if (!contact.id) {
				console.error('Cannot remove contact without ID');
				return;
			}
			if (state.contactIds.length === 1) {
				fullscreenModal.history.goBack();
			}
			dispatch({ type: 'removeRecipient', value: contact.id });
		},
		[state.contactIds.length, fullscreenModal.history, dispatch]
	);

	const renderRecipientItem = useCallback(
		(contact: Api.IContact) => {
			let companyLine = '';
			if (contact.companyName != null) {
				companyLine = contact.jobTitle != null ? `${contact.jobTitle} at ${contact.companyName}` : contact.companyName;
			}
			const nameLabel = getDisplayNameForBulkEmail(contact);
			return (
				<div
					className={css(
						bs.flex,
						bs.itemsCenter,
						bs.border0,
						bs.borderB,
						bs.borderSolid,
						bs.borderGray200,
						bs.cursorPointer,
						bs.py5,
						bs.px0,
						bs.relative
					)}
					key={contact.id}
				>
					<DeprecatedCloseButton
						onClick={ev => {
							ev.stopPropagation();
							handleRemoveRecipient(contact);
						}}
						aria-label={`Remove ${nameLabel} from recipients`}
					/>
					<Avatar2
						styleDeclaration={styleSheet.recipientsListItemAvatar}
						imgSrc={contact.profilePic}
						fallbackText={getPrincipalInitials(contact)}
					/>

					<div className={css(bs.flex1, bs.textSm, bs.ml4, bs.overflowHidden)}>
						<div className={css(styleSheet.recipientsListItemDetailsName)}>{nameLabel}</div>
						<div className={css(styleSheet.recipientsListItemDetailsCompanyName, bs.truncateText)}>{companyLine}</div>
					</div>
					{!contact.canReceiveText ? (
						<PhoneMissingPill styleDeclarations={[bs.absolute, bs.bottom2, bs.right2]} />
					) : null}
				</div>
			);
		},
		[handleRemoveRecipient]
	);

	const handleNext = useCallback(() => {
		if (!templateQuery.data) return;

		fullscreenModal.history.push({
			pathname: '/texting-campaign/schedule-send',
			state: {
				request: {
					campaignName: templateQuery.data.name,
					content: editorState.contentEditorState.getRawRichTextContent(),
					autoStart: false,
					putOnHold: false,
					skipIfInProgress: false,
					skipIfInPrevious: false,
					sendFromOptions: {
						mode: sendFrom?.sender?.mode,
						selectedUser: sendFromUserId ?? undefined,
						connectionType: sendFrom?.sender?.connectionType,
					},
					includeContactIds: state.contactIds,
					excludeContactIds: [],
					attachments: editorState.storedAttachments.length > 0 ? editorState.storedAttachments : undefined,
					groupByHousehold: false,
				},
				files: editorState.attachmentFiles,
			},
		});
	}, [
		templateQuery.data,
		fullscreenModal.history,
		editorState.contentEditorState,
		editorState.storedAttachments,
		editorState.attachmentFiles,
		sendFrom,
		sendFromUserId,
		state.contactIds,
	]);

	if (templateQuery.isLoading) {
		return (
			<>
				<FabContext appearance={{ hidden: true }} />
				<div className={css(bs.flex, bs.justifyCenter, bs.itemsCenter, bs.hFull)}>
					<LoadingSpinner type='large' />
				</div>
			</>
		);
	}

	if (templateQuery.isError || !templateQuery.data) {
		return (
			<>
				<FabContext appearance={{ hidden: true }} />
				<MultiContainerHeader
					fullscreenHeader='Draft Not Found'
					onFullscreenRequestClose={ev => {
						ev.preventDefault();
						fullscreenModal.dismissModal();
					}}
				/>
				<div className={css(bs.flex, bs.flexCol, bs.justifyCenter, bs.itemsCenter, bs.hFull, bs.textCenter)}>
					<h3 className={css(bs.textTitles, bs.textLg, bs.mb4)}>Draft not found</h3>
					<p className={css(bs.textLabel, bs.textSm)}>This draft may have been deleted or does not exist.</p>
				</div>
			</>
		);
	}

	return (
		<>
			<MultiContainerHeader
				fullscreenHeader='New Texting Campaign'
				onFullscreenRequestBack={ev => {
					ev.preventDefault();
					fullscreenModal.history.goBack();
				}}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			<CampaignComposerBaseLayout
				mainHeader={
					<div className={css(bs.px3, bs.py5, bs.flex, bs.gap2, bs.flexCol)}>
						<div className={css(bs.mb2)}>
							<h2 className={css(bs.m0)}>{templateQuery.data.name}</h2>
						</div>
						<SendFromDropdown
							disabled={!userSession.canSendOnBehalf}
							hideOptionsWithFilters
							canSendOnBehalf
							sendFromOption={sendFrom?.sender}
							sendOnBehalfOf={onSendOnBehalf}
							allowedContactBasedOptions='all'
						/>
					</div>
				}
				sideBar={
					<>
						<div className={css(styleSheet.recipientsHeader)}>
							<div className={css(styleSheet.recipientsHeaderTitle)}>
								<div>Recipients</div>
							</div>
							<div className={css(bs.flex, bs.itemsCenter, bs.flexWrap, bs.textSm, bs.justifyStart)}>
								<div className={css(bs.flex, bs.mr2)}>
									{numberOfRecipients != null
										? `${numberOfRecipients} out of ${state.contactIds.length} contact${
												state.contactIds.length !== 1 ? 's' : ''
											} with phone numbers`
										: null}
								</div>
							</div>
							<AutoCompleteSearchField
								anchorClassName='email-recipients-list-autocomplete-search-field-anchor'
								className='email-recipients-list-autocomplete-search-field'
								clearSearchFieldAfterSelectingItem={true}
								dropdownContentClassName={css(bs.wFull)}
								inputId='email-recipients-list-search-input'
								inputProps={{
									placeholder: 'Add additional contacts',
								}}
								leftAccessory={<SearchIcon className='email-recipients-list-autocomplete-search-field-icon' />}
								onItemSelected={(contact: Api.IContact) => {
									if (contact.id) {
										dispatch({ type: 'addRecipient', value: contact.id });
									}
								}}
								resultsLimit={6}
								type={Api.ResourceAutoCompleteViewModelType.Contact}
							/>
						</div>
						<div className={css(bs.flex, bs.flex1, bs.flexCol, bs.overflowXHidden, bs.overflowYAuto)}>
							<div>
								{contactQueries.map((contactQuery, i) => {
									if (!contactQuery.data) {
										return <LoadingSpinner key={i} type='small' />;
									}
									return renderRecipientItem(contactQuery.data);
								})}
							</div>
						</div>
						<div className={css(bs.py2, bs.px4, bs.border0, bs.borderT, bs.borderSolid, bs.borderGray200)}>
							<Checkbox
								id='textingCampaignAgreement-checkbox'
								className={css(bs.mb6)}
								checked={state.consentChecked}
								onChange={() => dispatch({ type: 'toggleConsentChecked' })}
							>
								<span className={css(bs.ml2)}>
									I agree that the recipients have opted in to receive texts and this is not a marketing campaign.
								</span>
							</Checkbox>
							<button
								className={css(bs.ctaButton, bs.wFull)}
								onClick={handleNext}
								disabled={!numberOfRecipients || !state.consentChecked}
							>
								<span>Next: Send Options</span>
							</button>
						</div>
					</>
				}
			>
				<>
					<FabContext appearance={{ hidden: true }} />
					<div
						className={css(
							bs.itemsCenter,
							bs.boxBorder,
							bs.flex,
							bs.flex1,
							bs.flexCol,
							bs.overflowAuto,
							bs.py8,
							bs.px6,
							bs.bgGray50
						)}
					>
						<div className={css(bs.flex, bs.flexCol, bs.maxWXl, bs.wFull, bs.mxAuto)}>
							<TextCampaignPageBody
								attachmentFiles={editorState.attachmentFiles}
								contentEditorState={editorState.contentEditorState}
								onAttachFiles={files => editorDispatch({ type: 'addAttachmentFiles', value: files })}
								onContentStateChanged={content =>
									editorDispatch({ type: 'setTextingContentEditorState', value: content })
								}
								onRemoveAttachmentFile={file => editorDispatch({ type: 'removeAttachmentFile', value: file })}
								onRemoveStoredAttachment={attachmentId =>
									editorDispatch({ type: 'removeStoredAttachment', value: attachmentId })
								}
								storedAttachments={editorState.storedAttachments}
							/>
						</div>
					</div>
				</>
			</CampaignComposerBaseLayout>
		</>
	);
}
